import { normalizeConfig } from "../shared/engine";
import { buildCalibrationSummary } from "../shared/calibration";
import { buildPerformanceSummary } from "../shared/performance";
import type {
  AdvisorPayload,
  BacktestRunRecord,
  CalibrationSummary,
  DataHealthSummary,
  GameAdvice,
  PerformanceSummary,
  RecommendationConfig,
  RecommendationSnapshotRecord,
  SourceAuditSummary,
  SourceQualitySummary,
  SourceSyncEvent,
  TicketHistoryRecord
} from "../shared/types";

export interface AppSettings {
  advisor_mode: RecommendationConfig["advisor_mode"];
  selection_goal: RecommendationConfig["selection_goal"];
  budget: number;
  tax_keep_rate: number;
  candidate_count: number;
  keep_top: number;
  quick_pick_share: number;
  draw_bias_mode: RecommendationConfig["draw_bias_mode"];
  rotation_mode: RecommendationConfig["rotation_mode"];
  use_powerplay: boolean;
}

export interface AppBundle {
  advisor: AdvisorPayload;
  history: TicketHistoryRecord[];
  performance: PerformanceSummary;
  calibration: CalibrationSummary;
  snapshots: RecommendationSnapshotRecord[];
  backtests: BacktestRunRecord[];
  dataHealth: DataHealthSummary;
  sourceAudit: SourceAuditSummary;
  sourceQuality: SourceQualitySummary;
  syncLog: SourceSyncEvent[];
  source: "live" | "local";
}

export interface ResultCheckPayload {
  history: TicketHistoryRecord[];
  performance: PerformanceSummary;
  calibration?: CalibrationSummary;
}

export interface AuthStatus {
  protected: boolean;
  authorized: boolean;
  pin_login?: boolean;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
  }
}

export const defaultAppSettings: AppSettings = {
  advisor_mode: "single_player",
  selection_goal: "jackpot_first",
  budget: 50,
  tax_keep_rate: 0.58,
  candidate_count: 20_000,
  keep_top: 2_000,
  quick_pick_share: 0.7,
  draw_bias_mode: "auto",
  rotation_mode: "draw",
  use_powerplay: false
};

export function configFromSettings(settings: AppSettings): RecommendationConfig {
  const candidateCount = Math.min(20_000, Math.max(100, Math.trunc(settings.candidate_count)));
  return normalizeConfig({
    ...settings,
    advisor_mode: "single_player",
    user_id: "me",
    candidate_count: candidateCount,
    keep_top: Math.min(Math.max(100, Math.floor(candidateCount / 10)), Math.max(100, Math.trunc(settings.keep_top))),
    risk_aversion: 0,
    max_overlap: 2,
    random_seed_extra: ""
  });
}

function authHeaders(token: string): HeadersInit {
  return token ? { "x-admin-token": token } : {};
}

async function fetchJson<T>(path: string, token: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      ...authHeaders(token),
      ...(init.headers ?? {})
    }
  });
  if (!response.ok) throw new ApiError(response.status, `${response.status} ${response.statusText}`);
  return (await response.json()) as T;
}

export async function loadAppBundle(settings: AppSettings, token: string): Promise<AppBundle> {
  const advisor = await fetchJson<AdvisorPayload>("/api/advisor", token, {
    method: "POST",
    body: JSON.stringify({ config: configFromSettings(settings) })
  });
  const history = await fetchJson<ResultCheckPayload>("/api/registry/check-results", token, {
    method: "POST",
    body: JSON.stringify({})
  }).catch((error) => {
    if (error instanceof ApiError && [401, 403].includes(error.status)) throw error;
    return { history: [], performance: buildPerformanceSummary([]), calibration: buildCalibrationSummary([]) };
  });
  const snapshots = await loadRecommendationSnapshots(token).catch((error) => {
    if (error instanceof ApiError && [401, 403].includes(error.status)) throw error;
    return [];
  });
  const [backtests, sourceQuality, sourceAudit, syncLog, calibration] = await Promise.all([
    loadBacktests(token).catch(() => []),
    loadSourceQuality(token).catch(() => advisor.source_quality),
    loadSourceAudit(token).catch(() => advisor.source_audit ?? {
      generated_at: advisor.generated_at,
      status: "watch" as const,
      issues: []
    }),
    loadSyncLog(token).catch(() => []),
    loadCalibration(token).catch(() => history.calibration ?? buildCalibrationSummary(history.history))
  ]);
  return {
    advisor,
    history: history.history,
    performance: history.performance ?? buildPerformanceSummary(history.history),
    calibration,
    snapshots,
    backtests,
    dataHealth: advisor.data_health,
    sourceAudit,
    sourceQuality,
    syncLog,
    source: "live"
  };
}

export async function getAuthStatus(token: string): Promise<AuthStatus> {
  return fetchJson<AuthStatus>("/api/auth-status", token);
}

export async function loginWithCode(code: string): Promise<{ ok: boolean; expires_at: string }> {
  return fetchJson<{ ok: boolean; expires_at: string }>("/api/login", "", { method: "POST", body: JSON.stringify({ code }) });
}

export async function runLiveUpdate(token: string): Promise<Record<string, unknown>> {
  return fetchJson<Record<string, unknown>>("/api/update", token, { method: "POST", body: JSON.stringify({}) });
}

export async function markTicketPlayed(token: string, advice: GameAdvice): Promise<TicketHistoryRecord> {
  const response = await fetchJson<{ ticket: TicketHistoryRecord }>("/api/registry/mark-played", token, {
    method: "POST",
    body: JSON.stringify({ advice })
  });
  return response.ticket;
}

export async function markAllTicketsPlayed(token: string, advices: GameAdvice[]): Promise<ResultCheckPayload> {
  const response = await fetchJson<ResultCheckPayload & { tracked: number }>("/api/registry/mark-all-played", token, {
    method: "POST",
    body: JSON.stringify({ advices })
  });
  return {
    history: response.history,
    performance: response.performance ?? buildPerformanceSummary(response.history),
    calibration: response.calibration ?? buildCalibrationSummary(response.history)
  };
}

export async function restoreLatestSnapshot(token: string): Promise<ResultCheckPayload> {
  const response = await fetchJson<ResultCheckPayload & { restored_from: string; tracked: number }>("/api/registry/restore-latest-snapshot", token, {
    method: "POST",
    body: JSON.stringify({})
  });
  return {
    history: response.history,
    performance: response.performance ?? buildPerformanceSummary(response.history),
    calibration: response.calibration ?? buildCalibrationSummary(response.history)
  };
}

export async function saveTicketResult(
  token: string,
  ticket: Pick<TicketHistoryRecord, "game_key" | "draw_date" | "compact">,
  result: Record<string, unknown>
): Promise<TicketHistoryRecord> {
  const response = await fetchJson<{ ticket: TicketHistoryRecord }>("/api/registry/result", token, {
    method: "POST",
    body: JSON.stringify({ ...ticket, result })
  });
  return response.ticket;
}

export async function checkTicketResults(token: string): Promise<ResultCheckPayload> {
  const response = await fetchJson<ResultCheckPayload>("/api/registry/check-results", token, {
    method: "POST",
    body: JSON.stringify({})
  });
  return { history: response.history, performance: response.performance ?? buildPerformanceSummary(response.history), calibration: response.calibration ?? buildCalibrationSummary(response.history) };
}

export async function loadRecommendationSnapshots(token: string, limit = 12): Promise<RecommendationSnapshotRecord[]> {
  const response = await fetchJson<{ snapshots: RecommendationSnapshotRecord[] }>(`/api/recommendation-snapshots?limit=${limit}`, token);
  return response.snapshots;
}

export async function loadBacktests(token: string, limit = 5): Promise<BacktestRunRecord[]> {
  const response = await fetchJson<{ backtests: BacktestRunRecord[] }>(`/api/backtests?limit=${limit}`, token);
  return response.backtests;
}

export async function runBacktest(token: string, settings: AppSettings, limitPerGame = 20): Promise<BacktestRunRecord[]> {
  const response = await fetchJson<{ backtest: BacktestRunRecord; backtests: BacktestRunRecord[] }>("/api/backtests/run", token, {
    method: "POST",
    body: JSON.stringify({ config: configFromSettings(settings), limit_per_game: limitPerGame })
  });
  return response.backtests;
}

export async function loadSourceQuality(token: string): Promise<SourceQualitySummary> {
  const response = await fetchJson<{ source_quality: SourceQualitySummary }>("/api/source-quality", token);
  return response.source_quality;
}

export async function loadDataHealth(token: string): Promise<DataHealthSummary> {
  const response = await fetchJson<{ data_health: DataHealthSummary }>("/api/data-health", token);
  return response.data_health;
}

export async function loadSourceAudit(token: string): Promise<SourceAuditSummary> {
  const response = await fetchJson<{ source_audit: SourceAuditSummary }>("/api/source-audit", token);
  return response.source_audit;
}

export async function loadCalibration(token: string): Promise<CalibrationSummary> {
  const response = await fetchJson<{ calibration: CalibrationSummary }>("/api/calibration", token);
  return response.calibration;
}

export async function loadSyncLog(token: string, limit = 60): Promise<SourceSyncEvent[]> {
  const response = await fetchJson<{ sync_log: SourceSyncEvent[] }>(`/api/sync-log?limit=${limit}`, token);
  return response.sync_log;
}
